/**
 * Platform detection — MXR-005 · 2026-08-05
 *
 * A single source of truth for "am I running inside the Capacitor
 * native shell?" so business logic never re-implements the check.
 *
 * Callers should prefer these helpers over `window.Capacitor?.isNativePlatform()`
 * so the semantics stay consistent — e.g. when we later add "in
 * TestFlight beta build?" detection, only this file changes.
 *
 * Design constraint (ADR-UX-001):
 *   · Business logic MUST NOT diverge between native and web. These
 *     helpers exist for presentation and integration branching only
 *     (OAuth redirect URL, back-button handling, share-sheet vs
 *     Web-Share API, deep-link handlers).
 */

// Capacitor injects a `window.Capacitor` global with `isNativePlatform()`
// when running inside the native WebView. On the web this is undefined.
// We defensively check both to avoid SSR/DEV crashes.

/**
 * True iff we're running inside the Capacitor native shell (iOS or Android).
 * Never returns true on the plain web (browser, PWA installed via Add to
 * Home Screen — that's still `web` platform in Capacitor's terminology).
 */
export function isNativePlatform() {
  if (typeof window === "undefined") return false;
  const cap = window.Capacitor;
  return Boolean(cap?.isNativePlatform && cap.isNativePlatform());
}

/**
 * Returns 'ios' | 'android' | 'web'. Never throws.
 */
export function getPlatform() {
  if (typeof window === "undefined") return "web";
  const cap = window.Capacitor;
  const p = cap?.getPlatform?.();
  return p === "ios" || p === "android" ? p : "web";
}

export const isIOS = () => getPlatform() === "ios";
export const isAndroid = () => getPlatform() === "android";
export const isWeb = () => getPlatform() === "web";
